import React, { useEffect } from 'react';
import { FormWrapper } from '../form-wrapper';
import { TypedUseSelectorHook } from 'react-redux';
import { StoreProps } from '../../services/constants';
import {
  ICountryInfo,
  IProductVariant,
  IStoreProduct,
} from '../../interface/storeInterface';
import {
  setHideProduct,
  setHideProductThankYou,
  setMandatoryProduct,
} from '../../redux/slices/commonSlice';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';

interface CssData {
  id: string;
  name?: string;
  link: string;
  added?: boolean;
  codeEditor?: boolean;
}

interface ScriptData {
  id: string;
  name?: string;
  link: string;
  added?: boolean;
  codeEditor?: boolean;
  countryList: ICountryInfo;
}
export interface CartProductsProps {
  enableMandatory: boolean;
  product?: IStoreProduct | null;
  variant?: IProductVariant | null;
}
export interface HideCartProductsProps {
  hideProduct: boolean;
  product?: IStoreProduct | null;
  variant?: IProductVariant | null;
}

type RenderPageProps = {
  children: React.ReactNode;
  fontFamily: string;
  errorColor: string;
  customCss: Array<CssData>;
  themeColor: { r: number; g: number; b: number };
  backgroundColor: { r: number; g: number; b: number };
  customScripts: Array<ScriptData>;
  useAppSelector: TypedUseSelectorHook<StoreProps>;
  useAppDispatch: () => ThunkDispatch<StoreProps, unknown, AnyAction>;
  addProduct?: CartProductsProps;
  hideProduct?: HideCartProductsProps;
  hideProductThankYou?: HideCartProductsProps;
  asyncLoader?: boolean;
  defer?: boolean;
};

const PuckRenderPage: React.FC<RenderPageProps> = ({
  children,
  fontFamily,
  errorColor = '#FF5A50',
  customCss,
  customScripts,
  themeColor,
  backgroundColor,
  useAppSelector,
  addProduct,
  hideProduct,
  hideProductThankYou,
  useAppDispatch,
  asyncLoader,
  defer,
}) => {
  const width = useAppSelector(state => state.store.currentWidth);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (addProduct?.enableMandatory && addProduct?.variant?.id) {
      dispatch(
        setMandatoryProduct(
          'gid://shopify/ProductVariant/' + addProduct?.variant?.id,
        ),
      );
    } else {
      dispatch(setMandatoryProduct(''));
    }
    return () => {
      dispatch(setMandatoryProduct(''));
    };
  }, [addProduct?.enableMandatory, addProduct?.variant?.id]);

  useEffect(() => {
    if (hideProduct?.hideProduct && hideProduct?.variant?.id) {
      dispatch(
        setHideProduct(
          'gid://shopify/ProductVariant/' + hideProduct?.variant?.id,
        ),
      );
    } else {
      dispatch(setHideProduct(''));
    }
    return () => {
      dispatch(setHideProduct(''));
    };
  }, [hideProduct?.hideProduct, hideProduct?.variant?.id]);

  useEffect(() => {
    if (hideProductThankYou?.hideProduct && hideProductThankYou?.variant?.id) {
      dispatch(
        setHideProductThankYou(
          'gid://shopify/ProductVariant/' + hideProductThankYou?.variant?.id,
        ),
      );
    } else {
      dispatch(setHideProductThankYou(''));
    }
    return () => {
      dispatch(setHideProductThankYou(''));
    };
  }, [hideProductThankYou?.hideProduct, hideProductThankYou?.variant?.id]);

  useEffect(() => {
    if (customCss) {
      customCss.map((css: CssData) => {
        if (css.name) {
          if (document.getElementById(`custom-css-${css.id}`)) {
            document.getElementById(`custom-css-${css.id}`)?.remove();
          }
          const cssElement = document.createElement('style');
          cssElement.id = `custom-css-${css.id}`;
          cssElement.textContent = `.puck-page{
          ${css.link}
        }`;
          document.head.appendChild(cssElement);
        }
      });
    }
  }, [customCss]);

  useEffect(() => {
    if (customScripts) {
      customScripts.map((script: ScriptData) => {
        if (script.name === 'Manual Script') {
          document.getElementById(`custom-script-${script.id}`)?.remove();
        }
        if (
          script.added &&
          !document.getElementById(`custom-script-${script.id}`)
        ) {
          const scriptElement = document.createElement('script');
          scriptElement.id = `custom-script-${script.id}`;
          if (!script?.codeEditor) {
            scriptElement.src = script.link;
          } else {
            scriptElement.textContent = script.link;
          }
          if (asyncLoader) {
            scriptElement.setAttribute('async', 'true');
          }
          if (defer) {
            scriptElement.setAttribute('defer', 'true');
          }
          document.body.appendChild(scriptElement);
        }
      });
    }
  }, [customScripts, asyncLoader, defer]);

  return (
    <FormWrapper
      useAppSelector={useAppSelector}
      useAppDispatch={useAppDispatch}>
      <div
        className={`puck-page dropzone-height ${width <= 640 ? 'responsive-font' : ''}`}
        style={
          {
            '--error-color': errorColor ?? '#FF5A50',
            '--color-page-builder-theme': `${themeColor?.r} ${themeColor?.g} ${themeColor?.b}`,
            '--template-background-color': `${backgroundColor}`,
            fontFamily: fontFamily,
            height: '100vh',
          } as React.CSSProperties
        }>
        {customCss &&
          customCss.map(
            (css: CssData, index: number) =>
              !css.name && (
                <link
                  key={index}
                  rel="stylesheet"
                  type="text/css"
                  href={css.link}></link>
              ),
          )}
        {children}
      </div>
    </FormWrapper>
  );
};

export default PuckRenderPage;
